import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { konfirmasi } from "../lib/dialogAplikasi";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, Paginasi } from "../components/etams/PalangDaftar";
import { salahFormulir, bersihkanNomor } from "../lib/rekeningResmi";

/* T107 (titipan auditor 1 Nov 2026) — MASTER REKENING RESMI CABANG (`bank_accounts`).
   Rekening ini yang dilihat customer di Ruang Pesanan saat transfer dan yang dipilih staf di formulir transfer
   manual (K234). Owner menambah, mengubah, menjadikan utama, menonaktifkan/mengaktifkan — TIDAK ADA hapus.
   Kepala Cabang melihat rekening cabang di wilayahnya, tanpa tombol. Nomor TAMPIL SEBAGIAN; nomor lengkap hanya di
   Jejak Audit. Tiap perubahan dikabarkan ke Owner. Penjaga sungguhan di server:
   GET/POST/PATCH /api/master/rekening-resmi (+ /{id}/jadikan-utama, /nonaktifkan, /aktifkan). */

const KOLOM = [
  { kunci: "nama_cabang", judul: "Cabang" },
  { kunci: "bank_name", judul: "Bank" },
  { kunci: "nomor_samaran", judul: "Nomor" },
  { kunci: "account_holder_name", judul: "Atas nama" },
  { kunci: "is_primary", judul: "Utama", ambil: (r) => (r.is_primary ? "Utama" : "") },
  { kunci: "active", judul: "Status", ambil: (r) => (r.active ? "Aktif" : "Nonaktif") },
];

function ringkas(r) {
  return `${r.bank_name} ${r.nomor_samaran} a.n. ${r.account_holder_name}`;
}

export default function MasterRekeningResmi() {
  const [items, setItems] = useState([]);
  const [cabang, setCabang] = useState([]);
  const [bolehUbah, setBolehUbah] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);       // null | { baru: true } | { rekening }
  const [sibuk, setSibuk] = useState(null);

  // Master KECIL (≤ beberapa rekening per cabang): cari/halaman di klien, urutan dari server
  // (cabang → aktif → utama → urutan).
  const daftar = useDaftar(items, { kunciCari: ["nama_cabang", "bank_name", "account_holder_name", "empat_akhir"], kolom: KOLOM });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/master/rekening-resmi");
      setItems(r.data.items || []);
      setCabang(r.data.cabang || []);
      setBolehUbah(!!r.data.boleh_ubah);
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat rekening resmi cabang.")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function aksi(r, jalur, tanya, pesan, bahaya = false) {
    if (!(await konfirmasi(tanya, { judul: "Rekening resmi cabang", ya: "Ya, lanjutkan", bahaya }))) return;
    setSibuk(r.id);
    try {
      await api.post(`/master/rekening-resmi/${r.id}/${jalur}`);
      toast.success(pesan);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Perubahan ditolak.")); }
    finally { setSibuk(null); }
  }

  const tanpaRekening = cabang.filter((c) => c.jumlah_aktif === 0);
  const tanpaUtama = cabang.filter((c) => c.jumlah_aktif > 0 && !c.punya_utama);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="master-rekening-resmi">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Rekening Resmi Cabang</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Rekening tujuan transfer yang dilihat customer di Ruang Pesanan dan yang dipilih staf saat mencatat
            transfer manual. Rekening utama tampil paling atas. Tidak ada hapus — rekening yang tidak dipakai lagi
            dinonaktifkan. Nomor tampil sebagian; nomor lengkap tercatat di Jejak Audit. Setiap perubahan dikabarkan
            ke Owner.
          </p>
        </div>
        {bolehUbah && (
          <Button onClick={() => setForm({ baru: true })} data-testid="rek-tambah">
            <Plus size={16} className="mr-1" />Tambah rekening
          </Button>
        )}
      </div>

      {!loading && (tanpaRekening.length > 0 || tanpaUtama.length > 0) && (
        <Card className="p-3 text-sm flex gap-2 items-start border-warning-soft-border bg-warning-soft text-warning-soft-foreground"
              data-testid="rek-peringatan">
          <TriangleAlert size={16} className="mt-0.5 shrink-0" />
          <div>
            {tanpaRekening.length > 0 && (
              <div>Belum ada rekening aktif: {tanpaRekening.map((c) => c.nama).join(", ")} — customer cabang ini
                tidak melihat rekening tujuan transfer.</div>
            )}
            {tanpaUtama.length > 0 && (
              <div>Belum ada rekening utama: {tanpaUtama.map((c) => c.nama).join(", ")}.</div>
            )}
          </div>
        </Card>
      )}

      <PalangDaftar daftar={daftar} placeholder="Cari cabang / bank / atas nama / 4 angka terakhir…" testId="rek-palang"
                    ekspor={{ nama: "rekening_resmi_cabang", kolom: daftar.kolom }} />
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Cabang</th>
              <th className="text-left px-3 py-2">Bank</th>
              <th className="text-left px-3 py-2">Nomor</th>
              <th className="text-left px-3 py-2">Atas nama</th>
              <th className="text-left px-3 py-2">Status</th>
              {bolehUbah && <th className="text-right px-3 py-2">Aksi</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-3 py-4 text-muted-foreground">Memuat…</td></tr>}
            {!loading && daftar.total === 0 && <tr><td colSpan={6} className="px-3 py-4 text-muted-foreground">
              {daftar.cari.trim() ? "Tidak ada yang cocok." : "Belum ada rekening resmi."}</td></tr>}
            {!loading && daftar.tampil.map((r) => (
              <tr key={r.id} className={`border-t ${r.active ? "" : "text-muted-foreground"}`} data-testid={`rek-baris-${r.id}`}>
                <td className="px-3 py-2">{r.nama_cabang}</td>
                <td className="px-3 py-2">{r.bank_name}</td>
                <td className="px-3 py-2 font-mono whitespace-nowrap" data-testid={`rek-nomor-${r.id}`}>{r.nomor_samaran}</td>
                <td className="px-3 py-2">{r.account_holder_name}</td>
                <td className="px-3 py-2 space-x-1 whitespace-nowrap">
                  {r.is_primary && <Badge data-testid={`rek-utama-${r.id}`}>Utama</Badge>}
                  {r.active ? <Badge variant="secondary">Aktif</Badge> : <Badge variant="outline">Nonaktif</Badge>}
                </td>
                {bolehUbah && (
                  <td className="px-3 py-2 text-right whitespace-nowrap space-x-1">
                    <Button size="sm" variant="outline" disabled={sibuk === r.id} onClick={() => setForm({ rekening: r })}
                            data-testid={`rek-ubah-${r.id}`}>Ubah</Button>
                    {r.active && !r.is_primary && (
                      <Button size="sm" variant="outline" disabled={sibuk === r.id} data-testid={`rek-jadi-utama-${r.id}`}
                              onClick={() => aksi(r, "jadikan-utama",
                                `Jadikan ${ringkas(r)} rekening UTAMA cabang ${r.nama_cabang}? Rekening utama yang lama tetap aktif, hanya tidak lagi tampil paling atas.`,
                                "Rekening utama diganti.")}>
                        Jadikan utama
                      </Button>
                    )}
                    <Button size="sm" variant="outline" disabled={sibuk === r.id} data-testid={`rek-saklar-${r.id}`}
                            onClick={() => (r.active
                              ? aksi(r, "nonaktifkan",
                                `Nonaktifkan ${ringkas(r)} di cabang ${r.nama_cabang}? Customer dan staf tidak melihatnya lagi sebagai tujuan transfer.${r.is_primary ? " Rekening ini rekening UTAMA — pilih rekening utama baru sesudahnya." : ""}`,
                                "Rekening dinonaktifkan.", true)
                              : aksi(r, "aktifkan",
                                `Aktifkan kembali ${ringkas(r)} di cabang ${r.nama_cabang}? Customer akan melihatnya lagi sebagai tujuan transfer.`,
                                "Rekening diaktifkan kembali."))}>
                      {r.active ? "Nonaktifkan" : "Aktifkan"}
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <Paginasi daftar={daftar} testId="rek-paginasi" />
      </Card>

      {form && (
        <FormRekening rekening={form.rekening} cabang={cabang}
                      onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
      )}
    </div>
  );
}

function FormRekening({ rekening, cabang, onClose, onSaved }) {
  const baru = !rekening;
  const [f, setF] = useState(() => ({
    store_id: rekening?.store_id || (cabang.length === 1 ? cabang[0].store_id : ""),
    bank: rekening?.bank_name || "",
    nomor: "",
    atasNama: rekening?.account_holder_name || "",
    utama: false,
    catatan: rekening?.notes || "",
  }));
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const salah = (baru && !f.store_id) ? "Pilih cabang." : salahFormulir(f, { nomorWajib: baru });

  async function simpan() {
    if (salah) { toast.error(salah); return; }
    const nomorBaru = bersihkanNomor(f.nomor);
    const namaCabang = cabang.find((c) => c.store_id === f.store_id)?.nama || rekening?.nama_cabang;
    const tanya = baru
      ? `Tambahkan rekening ${f.bank.trim()} •••• ${nomorBaru.slice(-4)} a.n. ${f.atasNama.trim()} untuk cabang ${namaCabang}? Pastikan nomornya benar — customer mentransfer ke rekening ini.`
      : `Simpan perubahan rekening cabang ${namaCabang}?${nomorBaru ? ` Nomor baru berakhiran ${nomorBaru.slice(-4)} — pastikan benar, customer mentransfer ke rekening ini.` : ""}`;
    if (!(await konfirmasi(tanya, { judul: "Rekening resmi cabang", ya: "Ya, simpan" }))) return;
    setSaving(true);
    try {
      if (baru) {
        await api.post("/master/rekening-resmi", {
          store_id: f.store_id, bank_name: f.bank, account_number: f.nomor, account_holder_name: f.atasNama,
          is_primary: f.utama, notes: f.catatan.trim() || null,
        });
        toast.success("Rekening ditambahkan.");
      } else {
        const badan = { bank_name: f.bank, account_holder_name: f.atasNama, notes: f.catatan.trim() || null };
        if (nomorBaru) badan.account_number = f.nomor;
        await api.patch(`/master/rekening-resmi/${rekening.id}`, badan);
        toast.success("Rekening diperbarui.");
      }
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Rekening belum tersimpan.")); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="rek-form">
        <DialogHeader><DialogTitle>{baru ? "Tambah rekening resmi" : `Ubah rekening — ${rekening.nama_cabang}`}</DialogTitle></DialogHeader>
        <div className="grid gap-3 py-2">
          {baru ? (
            <div>
              <Label htmlFor="rek-cabang">Cabang *</Label>
              <select id="rek-cabang" className="w-full border rounded-md h-9 px-2 bg-background text-sm"
                      value={f.store_id} onChange={(e) => upd("store_id", e.target.value)} data-testid="rek-form-cabang">
                <option value="">— pilih cabang —</option>
                {cabang.map((c) => <option key={c.store_id} value={c.store_id}>{c.nama}</option>)}
              </select>
            </div>
          ) : null}
          <div>
            <Label htmlFor="rek-bank">Nama bank *</Label>
            <Input id="rek-bank" value={f.bank} onChange={(e) => upd("bank", e.target.value)} placeholder="cth: BCA"
                   data-testid="rek-form-bank" />
          </div>
          <div>
            <Label htmlFor="rek-nomor">Nomor rekening {baru ? "*" : ""}</Label>
            <Input id="rek-nomor" inputMode="numeric" autoComplete="off" value={f.nomor}
                   onChange={(e) => upd("nomor", e.target.value)}
                   placeholder={baru ? "angka saja, cth: 1234567890" : `nomor sekarang ${rekening.nomor_samaran} — kosongkan bila tetap`}
                   data-testid="rek-form-nomor" />
            <p className="text-xs text-muted-foreground mt-1">Hanya angka, 5–20 digit. Spasi dan tanda hubung dibuang otomatis.</p>
          </div>
          <div>
            <Label htmlFor="rek-atas-nama">Atas nama *</Label>
            <Input id="rek-atas-nama" value={f.atasNama} onChange={(e) => upd("atasNama", e.target.value)}
                   placeholder="nama pemilik rekening persis seperti di buku tabungan" data-testid="rek-form-atas-nama" />
          </div>
          <div>
            <Label htmlFor="rek-catatan">Catatan</Label>
            <Input id="rek-catatan" value={f.catatan} onChange={(e) => upd("catatan", e.target.value)}
                   placeholder="boleh kosong" data-testid="rek-form-catatan" />
          </div>
          {baru && (
            <div className="flex items-center gap-2">
              <Checkbox id="rek-utama" checked={f.utama} onCheckedChange={(v) => upd("utama", !!v)} data-testid="rek-form-utama" />
              <Label htmlFor="rek-utama" className="cursor-pointer">Jadikan rekening utama cabang ini (utama yang lama dilepas)</Label>
            </div>
          )}
          {salah && (f.bank || f.nomor || f.atasNama) && <p className="text-xs text-destructive" data-testid="rek-form-salah">{salah}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={simpan} disabled={saving || !!salah} data-testid="rek-form-simpan">{saving ? "Menyimpan…" : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
